import { useEffect, useRef } from 'react';
import { xor } from 'lodash';
import { isEmpty } from 'lodash/lang';

import {
  ROLE_PRINCIPAL_INVESTIGATOR,
  ROLE_STUDY_MANAGER,
  ROLE_SUB_INVESTIGATOR
} from '../../../../../constants/userRoles';
import { userHasRole } from '../../../../../services/auth';
import store from '../../../../../store';
import { generateUrlByKey } from '../../../../root/router';

import { ADVERSE_EVENTS_WIDGET, AE, CANCELED, COMPLETE, DEVIATION_WIDGET, DV, ENCOUNTER_WIDGET } from './constants';

export function getValidationErrors(filter) {
  const errors = [];
  isEmpty(filter.selectedStudies) && errors.push('selectedStudies');
  isEmpty(filter.selectedSites) && errors.push('selectedSites');
  return errors;
}

export function isSelectedStudySitesChanged(oldFilters, newFilters) {
  return !isEmpty(
    xor(
      oldFilters.selectedStudySites.map(studySite => studySite.id),
      newFilters.selectedStudySites.map(studySite => studySite.id)
    )
  );
}

export function getAllUniqueEncountersFromCurrentTable(uploadedData) {
  let encounters = [];
  uploadedData
    .map(tableRow => ({
      studyName: tableRow.studyName,
      encounterName: tableRow.encounterName
    }))
    .filter(function(item) {
      let i = encounters.findIndex(x => x.studyName === item.studyName && x.encounterName === item.encounterName);
      if (i <= -1) {
        encounters.push(item);
      }
      return null;
    });
  return encounters;
}

export function usePrevious(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}
export const getUrlToReviewPage = (original, worklistName) => {
  const {
    ssuPatientId,
    patientEncounterId,
    patientIdentifier,
    hasInvestigatorReview,
    groupAssignId,
    encounterStatus
  } = original;

  if (!groupAssignId || !hasInvestigatorReview) {
    return;
  }
  if (
    worklistName === 'Investigator Worklist' &&
    (!userHasRole([ROLE_PRINCIPAL_INVESTIGATOR, ROLE_SUB_INVESTIGATOR]) || encounterStatus === 'SM_REVIEW')
  ) {
    return;
  }
  if (
    worklistName === 'Study Manager Worklist' &&
    (!userHasRole(ROLE_STUDY_MANAGER) || encounterStatus === 'PI_REVIEW')
  ) {
    return;
  }

  return generateUrlByKey(
    `${worklistName}.Patient Profile.Patient Studies.Encounter Details.Investigator Encounter Review`,
    {
      ssuPatientId: ssuPatientId,
      patientId: patientIdentifier,
      patientEncounterId,
      groupAssignId,
      signatureId: 'current'
    }
  );
};

export const getUrlToEncounterDetailsPage = (original, worklistName) => {
  const { patientIdentifier, ssuPatientId, patientEncounterId } = original;
  if (!!patientEncounterId) {
    return generateUrlByKey(`${worklistName}.Patient Profile.Patient Studies.Encounter Details`, {
      patientId: patientIdentifier,
      ssuPatientId,
      patientEncounterId
    });
  }
};

export const getUrlToPatientInfoPage = (original, worklistName) => {
  const { patientIdentifier, ssuPatientId } = original;
  return generateUrlByKey(`${worklistName}.Patient Profile.Patient Studies`, {
    patientId: patientIdentifier,
    ssuPatientId: ssuPatientId
  });
};

export const getStatusMessage = (currentSelectedStatus, statusesProvider, statusType) => {
  const resolveMessage = statusType => {
    switch (statusType) {
      case ENCOUNTER_WIDGET:
        return 'Encounters';
      case ADVERSE_EVENTS_WIDGET:
        return 'Adverse Events';
      case DEVIATION_WIDGET:
        return 'Protocol Deviations';
      default:
        return 'Encounters';
    }
  };
  if (currentSelectedStatus === CANCELED) {
    return `Canceled ${resolveMessage(statusType)}`;
  } else if (currentSelectedStatus === COMPLETE) {
    return `Complete ${resolveMessage(statusType)}`;
  } else {
    return `${statusesProvider
      .filter(status => status.id === currentSelectedStatus)
      .map(status => status.name)} for ${resolveMessage(statusType)}`;
  }
};

export const resolveDomainCodeForReviewPages = widgetType => {
  const {
    appInfo: {
      features: { deviationReviewCycleEnabled }
    }
  } = store.getState();

  if (!deviationReviewCycleEnabled) {
    return AE;
  }
  switch (widgetType) {
    case ADVERSE_EVENTS_WIDGET:
      return AE;
    case DEVIATION_WIDGET:
      return DV;
    default:
      throw new Error(`Widget type "${widgetType}" is not supported`);
  }
};

export const resolveEmptyValue = widgetType => {
  switch (widgetType) {
    case ADVERSE_EVENTS_WIDGET:
      return 'Adverse Event';
    case DEVIATION_WIDGET:
      return 'Protocol Deviation';
    default:
      throw new Error(`Widget type "${widgetType}" is not supported`);
  }
};

export const resolveLogNameHeader = widgetType => {
  switch (widgetType) {
    case ADVERSE_EVENTS_WIDGET:
      return AE;
    case DEVIATION_WIDGET:
      return 'Protocol Deviation';
    default:
      throw new Error(`Widget type "${widgetType}" is not supported`);
  }
};

export const resolveDomainFullName = domainCode => {
  switch (domainCode) {
    case AE:
      return 'Adverse Event';
    case DV:
      return 'Protocol Deviation';
    default:
      throw new Error(`Domain code "${domainCode}" is not supported`);
  }
};
